import { checkIsLoop, checkIsLoopEnd } from "@/core/util";
import { getLoopEndOrigId, getLoopSteps } from "@/flow/utils/loopUtils";
import { parseUiData } from "@/flow/utils/uiDataUtils";

export interface SkipState {
  directlySkipped?: boolean;
  inheritedSkipped?: boolean;
  inheritedFromOrigId?: string | null;
}

export interface SkipMenuState {
  isSkipped: boolean;
  isInheritedSkipped: boolean;
  canSkip: boolean;
  canEnable: boolean;
  enableDisabled: boolean;
}

type FlowEntity = Record<string, any>;

const MAX_NORMALIZE_PASSES = 20;

function normalizeOrigId(value: unknown): string {
  return value == null ? "" : String(value);
}

function parseUiDataObject(entity?: FlowEntity | null): Record<string, any> {
  const parsed = parseUiData<Record<string, any>>(entity?.uiData);
  return parsed && typeof parsed === "object" ? parsed : {};
}

function isObjectEmpty(value: Record<string, any>): boolean {
  return Object.keys(value).length === 0;
}

function writeUiDataObject(entity: FlowEntity, uiData: Record<string, any>) {
  if (isObjectEmpty(uiData)) {
    delete entity.uiData;
    return;
  }

  entity.uiData = JSON.stringify(uiData);
}

function getMutableSkipState(entity: FlowEntity): {
  uiData: Record<string, any>;
  skipState: SkipState;
} {
  const uiData = parseUiDataObject(entity);
  const raw = uiData.skipState;
  const skipState =
    raw && typeof raw === "object" ? ({ ...raw } as SkipState) : {};

  return { uiData, skipState };
}

function writeSkipState(entity: FlowEntity, skipState: SkipState) {
  const { uiData } = getMutableSkipState(entity);
  const nextState: SkipState = {};

  if (skipState.directlySkipped) {
    nextState.directlySkipped = true;
  }
  if (skipState.inheritedSkipped) {
    nextState.inheritedSkipped = true;
    nextState.inheritedFromOrigId = skipState.inheritedFromOrigId ?? null;
  }

  if (Object.keys(nextState).length) {
    uiData.skipState = nextState;
  } else {
    delete uiData.skipState;
  }

  writeUiDataObject(entity, uiData);
}

export function getSkipState(entity?: FlowEntity | null): SkipState {
  if (!entity) return {};
  return getMutableSkipState(entity).skipState;
}

export function isStepDirectlySkipped(entity?: FlowEntity | null): boolean {
  return getSkipState(entity).directlySkipped === true;
}

export function isStepInheritedSkipped(entity?: FlowEntity | null): boolean {
  return getSkipState(entity).inheritedSkipped === true;
}

export function isStepSkipped(entity?: FlowEntity | null): boolean {
  const skipState = getSkipState(entity);
  return (
    skipState.directlySkipped === true || skipState.inheritedSkipped === true
  );
}

export function setStepDirectlySkipped(entity?: FlowEntity | null): boolean {
  if (!entity || checkIsLoopEnd(entity)) return false;

  const skipState = getSkipState(entity);
  if (skipState.directlySkipped === true) return false;

  writeSkipState(entity, {
    ...skipState,
    directlySkipped: true,
  });
  return true;
}

function setStepInheritedSkipped(
  entity: FlowEntity,
  inheritedFromOrigId: string
): boolean {
  const skipState = getSkipState(entity);
  if (
    skipState.inheritedSkipped === true &&
    skipState.inheritedFromOrigId === inheritedFromOrigId
  ) {
    return false;
  }

  writeSkipState(entity, {
    ...skipState,
    inheritedSkipped: true,
    inheritedFromOrigId,
  });
  return true;
}

function clearInheritedSkip(entity: FlowEntity): boolean {
  const skipState = getSkipState(entity);
  if (!skipState.inheritedSkipped && !skipState.inheritedFromOrigId) {
    return false;
  }

  writeSkipState(entity, {
    ...skipState,
    inheritedSkipped: false,
    inheritedFromOrigId: null,
  });
  return true;
}

export function clearStepSkip(entity?: FlowEntity | null): boolean {
  if (!entity) return false;
  const hadSkip = isStepSkipped(entity);
  writeSkipState(entity, {});
  clearStepLinkSkipFlags(entity);
  return hadSkip;
}

export function clearStepLinkSkipFlags(entity?: FlowEntity | null): void {
  if (!entity) return;

  ["source", "target"].forEach((slotName) => {
    const slot = entity[slotName];
    const links = Array.isArray(slot?.stepLinkData) ? slot.stepLinkData : [];

    links.forEach((link: FlowEntity) => {
      if (!link) return;
      delete link.skip;
      delete link.skipped;
      delete link.skipState;
      if (link.connected === false) {
        link.connected = true;
      }
    });
  });
}

export function markStepLinkSkipped(link: FlowEntity): void {
  if (!link) return;
  link.connected = false;
}

export function collectFlowEntities(steps: any[] = []): FlowEntity[] {
  const result: FlowEntity[] = [];

  const visitSteps = (items: any[]) => {
    if (!Array.isArray(items)) return;

    items.forEach((step) => {
      if (!step) return;
      result.push(step);

      if (Array.isArray(step.routes)) {
        step.routes.forEach((route: FlowEntity) => {
          if (!route) return;
          result.push(route);
          visitSteps(route.steps || []);
        });
      }

      if (Array.isArray(step.steps)) {
        visitSteps(step.steps);
      }
    });
  };

  visitSteps(steps);
  return result;
}

function buildEntityMap(steps: any[]): Map<string, FlowEntity> {
  const byOrigId = new Map<string, FlowEntity>();

  collectFlowEntities(steps).forEach((entity) => {
    const origId = normalizeOrigId(entity?.origId);
    if (origId) byOrigId.set(origId, entity);
  });

  return byOrigId;
}

function getRouterChildSteps(router: FlowEntity): FlowEntity[] {
  if (!Array.isArray(router?.routes)) return [];

  return router.routes.flatMap((route: FlowEntity) =>
    Array.isArray(route?.steps)
      ? route.steps.filter((step: FlowEntity) => step && !checkIsLoopEnd(step))
      : []
  );
}

function getLoopMemberSteps(
  loop: FlowEntity,
  byOrigId: Map<string, FlowEntity>
): FlowEntity[] {
  return getLoopSteps(loop)
    .map((loopStep) => byOrigId.get(normalizeOrigId(loopStep?.origId)))
    .filter((step): step is FlowEntity =>
      Boolean(step && !checkIsLoopEnd(step))
    );
}

function getLoopEndStep(
  loop: FlowEntity,
  byOrigId: Map<string, FlowEntity>
): FlowEntity | null {
  const loopEndOrigId = normalizeOrigId(getLoopEndOrigId(loop));
  if (!loopEndOrigId) return null;

  return byOrigId.get(loopEndOrigId) ?? null;
}

function getContentPathReferenceOrigIds(contentPath: unknown): Set<string> {
  if (contentPath == null || contentPath === "") return new Set();

  let parsed: unknown = contentPath;
  if (typeof contentPath === "string") {
    try {
      parsed = JSON.parse(contentPath);
    } catch {
      return new Set();
    }
  }

  if (!Array.isArray(parsed)) return new Set();

  return new Set(
    parsed
      .map((item) =>
        item && typeof item === "object"
          ? normalizeOrigId((item as FlowEntity).origId)
          : ""
      )
      .filter(Boolean)
  );
}

export function getLoopContentPathReferenceOrigIds(
  loop: FlowEntity
): Set<string> {
  return getContentPathReferenceOrigIds(loop?.contentPath);
}

/**
 * Share this calculation in a diagram-owned computed. Read the tree, loop
 * metadata and content paths reactively so in-place edits invalidate it too.
 * Use the same loop/skip predicates as the per-step search below.
 */
export function collectActiveLoopReferenceOrigIds(
  steps: any[] = []
): ReadonlySet<string> {
  const references = new Set<string>();
  collectFlowEntities(steps).forEach((entity) => {
    if (!checkIsLoop(entity) || isStepSkipped(entity)) return;
    getLoopContentPathReferenceOrigIds(entity).forEach((id) =>
      references.add(id)
    );
  });
  return references;
}

export function isReferencedByActiveLoop(
  step: FlowEntity | null | undefined,
  steps: any[] = [],
  activeLoopReferenceOrigIds?: ReadonlySet<string>
): boolean {
  const origId = normalizeOrigId(step?.origId);
  if (!origId) return false;
  if (activeLoopReferenceOrigIds) {
    return activeLoopReferenceOrigIds.has(origId);
  }

  return collectFlowEntities(steps).some((entity) => {
    if (!checkIsLoop(entity) || isStepSkipped(entity)) return false;
    return getLoopContentPathReferenceOrigIds(entity).has(origId);
  });
}

function hasSkippedLoopContentPathReference(
  loop: FlowEntity | null | undefined,
  byOrigId: Map<string, FlowEntity>,
  ignoredInheritedFromOrigId = ""
): boolean {
  if (!loop || !checkIsLoop(loop)) return false;

  return Array.from(getLoopContentPathReferenceOrigIds(loop)).some((origId) => {
    const referencedStep = byOrigId.get(origId);
    if (!ignoredInheritedFromOrigId) return isStepSkipped(referencedStep);

    const skipState = getSkipState(referencedStep);
    return (
      skipState.directlySkipped === true ||
      (skipState.inheritedSkipped === true &&
        skipState.inheritedFromOrigId !== ignoredInheritedFromOrigId)
    );
  });
}

function hasSkippedLoopContentPathReferenceInFlow(
  loop: FlowEntity | null | undefined,
  steps: any[] = [],
  ignoredInheritedFromOrigId = ""
): boolean {
  if (!loop || !checkIsLoop(loop)) return false;
  return hasSkippedLoopContentPathReference(
    loop,
    buildEntityMap(steps),
    ignoredInheritedFromOrigId
  );
}

function propagateInheritedSkip(steps: any[]): boolean {
  let changed = false;

  collectFlowEntities(steps).forEach((entity) => {
    if (!entity?.origId || !isStepSkipped(entity)) return;

    const parentOrigId = normalizeOrigId(entity.origId);
    if (!parentOrigId) return;

    if (entity.type === "ROUTER" && Array.isArray(entity.routes)) {
      entity.routes.forEach((route: FlowEntity) => {
        changed = setStepInheritedSkipped(route, parentOrigId) || changed;
        collectFlowEntities(route.steps || []).forEach((child) => {
          changed = setStepInheritedSkipped(child, parentOrigId) || changed;
        });
      });
    }

    if (checkIsLoop(entity)) {
      const byOrigId = buildEntityMap(steps);
      getLoopMemberSteps(entity, byOrigId).forEach((member) => {
        changed = setStepInheritedSkipped(member, parentOrigId) || changed;
      });

      const loopEndStep = getLoopEndStep(entity, byOrigId);
      if (loopEndStep) {
        changed = setStepInheritedSkipped(loopEndStep, parentOrigId) || changed;
      }
    }
  });

  return changed;
}

function clearAllInheritedSkip(steps: any[]): boolean {
  let changed = false;
  collectFlowEntities(steps).forEach((entity) => {
    changed = clearInheritedSkip(entity) || changed;
  });
  return changed;
}

function promoteParents(steps: any[]): boolean {
  let changed = false;
  const byOrigId = buildEntityMap(steps);
  const entities = collectFlowEntities(steps);

  entities.forEach((entity) => {
    if (!checkIsLoop(entity) || isStepSkipped(entity)) return;

    if (hasSkippedLoopContentPathReference(entity, byOrigId)) {
      changed = setStepDirectlySkipped(entity) || changed;
    }
  });

  entities.forEach((entity) => {
    if (entity?.type !== "ROUTER" || isStepSkipped(entity)) return;

    const children = getRouterChildSteps(entity);
    if (children.length && children.every((child) => isStepSkipped(child))) {
      changed = setStepDirectlySkipped(entity) || changed;
    }
  });

  entities.forEach((entity) => {
    if (!checkIsLoop(entity) || isStepSkipped(entity)) return;

    const members = getLoopMemberSteps(entity, byOrigId);
    if (members.length && members.every((member) => isStepSkipped(member))) {
      changed = setStepDirectlySkipped(entity) || changed;
    }
  });

  return changed;
}

export function normalizeFlowSkipState(steps: any[] = []): boolean {
  if (!Array.isArray(steps) || !steps.length) return false;

  let changed = false;
  let pass = 0;

  do {
    changed = false;
    changed = clearAllInheritedSkip(steps) || changed;
    changed = propagateInheritedSkip(steps) || changed;
    changed = promoteParents(steps) || changed;
    pass += 1;
  } while (changed && pass < MAX_NORMALIZE_PASSES);

  changed = clearAllInheritedSkip(steps) || changed;
  changed = propagateInheritedSkip(steps) || changed;

  return changed;
}

function clearDescendantSkipState(parent: FlowEntity, steps: any[]) {
  if (parent.type === "ROUTER" && Array.isArray(parent.routes)) {
    parent.routes.forEach((route: FlowEntity) => {
      clearStepSkip(route);
      collectFlowEntities(route.steps || []).forEach((child) => {
        clearStepSkip(child);
      });
    });
  }

  if (checkIsLoop(parent)) {
    const byOrigId = buildEntityMap(steps);
    getLoopMemberSteps(parent, byOrigId).forEach((member) => {
      clearStepSkip(member);
    });

    const loopEndStep = getLoopEndStep(parent, byOrigId);
    if (loopEndStep) {
      clearStepSkip(loopEndStep);
    }
  }
}

export function enableSkippedStep(
  step: FlowEntity | null | undefined,
  steps: any[] = []
): boolean {
  if (!step || isStepInheritedSkipped(step)) return false;
  if (
    hasSkippedLoopContentPathReferenceInFlow(
      step,
      steps,
      normalizeOrigId(step.origId)
    )
  ) {
    return false;
  }

  const changed = clearStepSkip(step);
  clearDescendantSkipState(step, steps);
  normalizeFlowSkipState(steps);

  return changed;
}

export function skipStep(
  step: FlowEntity | null | undefined,
  steps: any[] = []
): boolean {
  if (!step || !canSkipStep(step, steps)) return false;

  const changed = setStepDirectlySkipped(step);
  normalizeFlowSkipState(steps);

  return changed;
}

export function canSkipStep(
  step: FlowEntity | null | undefined,
  steps: any[] = [],
  activeLoopReferenceOrigIds?: ReadonlySet<string>
): boolean {
  if (!step) return false;
  if (checkIsLoopEnd(step)) return false;
  if (isStepSkipped(step)) return false;
  if (isReferencedByActiveLoop(step, steps, activeLoopReferenceOrigIds)) {
    return false;
  }

  return true;
}

export function getSkipMenuState(
  step: FlowEntity | null | undefined,
  steps: any[] = [],
  activeLoopReferenceOrigIds?: ReadonlySet<string>
): SkipMenuState {
  const isSkipped = isStepSkipped(step);
  const isInheritedSkipped = isStepInheritedSkipped(step);
  const enableDisabled =
    isInheritedSkipped ||
    hasSkippedLoopContentPathReferenceInFlow(
      step,
      steps,
      normalizeOrigId(step?.origId)
    );

  return {
    isSkipped,
    isInheritedSkipped,
    canSkip: canSkipStep(step, steps, activeLoopReferenceOrigIds),
    canEnable: isSkipped && !enableDisabled,
    enableDisabled,
  };
}

export function findFirstUnskippedStep(
  steps: FlowEntity[] = []
): FlowEntity | undefined {
  return steps.find((step) => step?.origId && !isStepSkipped(step));
}
